import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  Palette, Globe, Type, Square, Mail, 
  Smartphone, Code, Save, Eye, Crown, Lock, Send, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import api from '../../utils/api';

const FONTS = ['System', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins'];
const BUTTON_STYLES = ['rounded', 'square', 'pill'];
const CARD_STYLES = ['flat', 'elevated', 'outlined'];
const ICON_STYLES = ['filled', 'outlined', 'rounded'];

// Common SMTP presets
const SMTP_PRESETS = [
  { name: 'Custom', host: '', port: 587, use_tls: true },
  { name: 'Gmail', host: 'smtp.gmail.com', port: 587, use_tls: true },
  { name: 'Outlook/Office 365', host: 'smtp.office365.com', port: 587, use_tls: true },
  { name: 'Yahoo', host: 'smtp.mail.yahoo.com', port: 587, use_tls: true },
  { name: 'Zoho', host: 'smtp.zoho.com', port: 587, use_tls: true },
  { name: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, use_tls: true },
  { name: 'Mailgun', host: 'smtp.mailgun.org', port: 587, use_tls: true },
  { name: 'Amazon SES', host: 'email-smtp.us-east-1.amazonaws.com', port: 587, use_tls: true },
];

const WhiteLabelSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  
  // Email/SMTP state
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpVerified, setSmtpVerified] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testMessage, setTestMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const subRes = await api.get('/subscription');
      setSubscription(subRes.data);
      
      const canAccess = subRes.data.plan_type === 'white_label' || subRes.data.status === 'trialing';
      setHasAccess(canAccess);
      
      if (canAccess) {
        const settingsRes = await api.get('/white-label/settings');
        setSettings(settingsRes.data);
        
        // Fetch SMTP settings
        try {
          const smtpRes = await api.get('/company/smtp-settings');
          if (smtpRes.data.configured) {
            setSmtpConfigured(true);
            setSmtpVerified(smtpRes.data.settings?.is_verified || false);
            setSmtpSettings({
              smtp_host: smtpRes.data.settings?.smtp_host || '',
              smtp_port: smtpRes.data.settings?.smtp_port || 587,
              smtp_username: smtpRes.data.settings?.smtp_username || '',
              smtp_password: '', // Password is not returned for security
              from_email: smtpRes.data.settings?.from_email || '',
              from_name: smtpRes.data.settings?.from_name || '',
              use_tls: smtpRes.data.settings?.use_tls ?? true
            });
          }
        } catch (err) {
          // SMTP not configured yet, that's fine
          console.log('SMTP not configured');
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
      } else {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  // Save SMTP settings
  const handleSaveSmtp = async () => {
    // Validation
    if (!smtpSettings.smtp_host || !smtpSettings.smtp_username || !smtpSettings.from_email || !smtpSettings.from_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!smtpSettings.smtp_password && !smtpConfigured) {
      toast.error('Password is required for new SMTP configuration');
      return;
    }
    
    setSavingSmtp(true);
    try {
      const payload = { ...smtpSettings };
      // Send null instead of empty string so backend keeps existing password
      if (!payload.smtp_password) {
        payload.smtp_password = null;
      }
      await api.post('/company/smtp-settings', payload);
      setSmtpConfigured(true);
      setSmtpVerified(false); // Need to test again after changes
      toast.success('Email settings saved! Please send a test email to verify.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  // Send test email
  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address to send the test to');
      return;
    }
    
    setTestingEmail(true);
    try {
      const res = await api.post('/company/smtp-settings/test', {
        test_email: testEmail,
        message: testMessage
      });
      toast.success(res.data.message || 'Test email sent successfully!');
      setSmtpVerified(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  // Apply SMTP preset
  const applySmtpPreset = (presetName) => {
    const preset = SMTP_PRESETS.find(p => p.name === presetName);
    if (preset && preset.name !== 'Custom') {
      setSmtpSettings({
        ...smtpSettings,
        smtp_host: preset.host,
        smtp_port: preset.port,
        use_tls: preset.use_tls
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/white-label/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="white-label-locked">
        <div>
          <h1 className="text-4xl font-bold mb-2">White Label Settings</h1>
          <p className="text-gray-600">Customize your branding and app appearance</p>
        </div>

        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="py-16 text-center">
            <Lock size={64} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold mb-2">White Label Plan Required</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Unlock custom branding, your own domain, and a white-label mobile app experience.
            </p>
            <Button onClick={() => window.location.href = '/control-panel/billing'}>
              <Crown size={18} className="mr-2" />
              Upgrade to White Label
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="white-label-settings">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold mb-2">White Label Settings</h1>
          <p className="text-gray-600">Customize your branding and app appearance</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save size={18} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {subscription?.status === 'trialing' && (
        <Card className="border-purple-300 bg-purple-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Crown className="text-purple-600" size={20} />
              <span className="font-medium">Trial Mode</span>
              <Badge variant="outline">
                {subscription.days_left_in_trial} days left
              </Badge>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              All White Label features are available during your trial.
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="branding">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="branding">
            <Palette size={16} className="mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail size={16} className="mr-2" />
            Email Settings
          </TabsTrigger>
          <TabsTrigger value="typography">
            <Type size={16} className="mr-2" />
            Typography
          </TabsTrigger>
          <TabsTrigger value="ui">
            <Square size={16} className="mr-2" />
            UI Styles
          </TabsTrigger>
          <TabsTrigger value="domain">
            <Globe size={16} className="mr-2" />
            Domain
          </TabsTrigger>
          <TabsTrigger value="app">
            <Smartphone size={16} className="mr-2" />
            Mobile App
          </TabsTrigger>
          <TabsTrigger value="css">
            <Code size={16} className="mr-2" />
            Custom CSS
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Brand Colors</CardTitle>
              <CardDescription>Set your brand colors for the entire application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings?.primary_color || '#3B82F6'}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings?.primary_color || '#3B82F6'}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings?.secondary_color || '#10B981'}
                      onChange={(e) => updateSetting('secondary_color', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings?.secondary_color || '#10B981'}
                      onChange={(e) => updateSetting('secondary_color', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Accent Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings?.accent_color || '#F59E0B'}
                      onChange={(e) => updateSetting('accent_color', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings?.accent_color || '#F59E0B'}
                      onChange={(e) => updateSetting('accent_color', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Gradient Start</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings?.gradient_start || '#3B82F6'}
                      onChange={(e) => updateSetting('gradient_start', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings?.gradient_start || '#3B82F6'}
                      onChange={(e) => updateSetting('gradient_start', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Gradient End</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings?.gradient_end || '#06B6D4'}
                      onChange={(e) => updateSetting('gradient_end', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings?.gradient_end || '#06B6D4'}
                      onChange={(e) => updateSetting('gradient_end', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="p-6 border rounded-lg">
                <h4 className="font-semibold mb-4">Preview</h4>
                <div 
                  className="h-24 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{
                    background: `linear-gradient(135deg, ${settings?.gradient_start || '#3B82F6'}, ${settings?.gradient_end || '#06B6D4'})`
                  }}
                >
                  Your Brand Gradient
                </div>
                <div className="flex gap-3 mt-4">
                  <div 
                    className="w-16 h-16 rounded flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: settings?.primary_color || '#3B82F6' }}
                  >
                    Primary
                  </div>
                  <div 
                    className="w-16 h-16 rounded flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: settings?.secondary_color || '#10B981' }}
                  >
                    Secondary
                  </div>
                  <div 
                    className="w-16 h-16 rounded flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: settings?.accent_color || '#F59E0B' }}
                  >
                    Accent
                  </div>
                </div>
              </div>

              {/* Logo Upload */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Logo URL</Label>
                  <Input
                    placeholder="https://example.com/logo.png"
                    value={settings?.logo_url || ''}
                    onChange={(e) => updateSetting('logo_url', e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Recommended: 200x50px PNG</p>
                </div>
                <div>
                  <Label>Favicon URL</Label>
                  <Input
                    placeholder="https://example.com/favicon.ico"
                    value={settings?.favicon_url || ''}
                    onChange={(e) => updateSetting('favicon_url', e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Recommended: 32x32px ICO/PNG</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email">
          <div className="space-y-6">
            {/* SMTP Configuration Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Email (SMTP) Configuration</CardTitle>
                    <CardDescription>Configure your own SMTP server to send emails from your domain</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {smtpConfigured && smtpVerified && (
                      <Badge variant="default" className="bg-green-600" data-testid="smtp-verified-badge">
                        <CheckCircle size={14} className="mr-1" /> Verified
                      </Badge>
                    )}
                    {smtpConfigured && !smtpVerified && (
                      <Badge variant="secondary" data-testid="smtp-unverified-badge">
                        <AlertCircle size={14} className="mr-1" /> Not Verified
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SMTP Preset Selector */}
                <div>
                  <Label>Email Provider Preset</Label>
                  <Select onValueChange={applySmtpPreset} data-testid="smtp-preset-select">
                    <SelectTrigger className="mt-2" data-testid="smtp-preset-trigger">
                      <SelectValue placeholder="Select a provider or use Custom..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SMTP_PRESETS.map(preset => (
                        <SelectItem key={preset.name} value={preset.name}>
                          {preset.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Select a preset to auto-fill server details, or choose Custom.</p>
                </div>

                {/* SMTP Server Details */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smtp-host">SMTP Host <span className="text-red-500">*</span></Label>
                    <Input
                      id="smtp-host"
                      data-testid="smtp-host-input"
                      placeholder="smtp.example.com"
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp-port">SMTP Port</Label>
                    <Input
                      id="smtp-port"
                      data-testid="smtp-port-input"
                      type="number"
                      placeholder="587"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) || 587 })}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-400 mt-1">587 = STARTTLS, 465 = SSL</p>
                  </div>
                </div>

                {/* Credentials */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smtp-username">Username <span className="text-red-500">*</span></Label>
                    <Input
                      id="smtp-username"
                      data-testid="smtp-username-input"
                      placeholder="your@email.com"
                      value={smtpSettings.smtp_username}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_username: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp-password">
                      Password <span className="text-red-500">*</span>
                      {smtpConfigured && <span className="text-xs text-gray-500 ml-2">(leave blank to keep current)</span>}
                    </Label>
                    <Input
                      id="smtp-password"
                      data-testid="smtp-password-input"
                      type="password"
                      placeholder={smtpConfigured ? '••••••••' : 'Enter SMTP password'}
                      value={smtpSettings.smtp_password}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* From Details */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="from-email">From Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="from-email"
                      data-testid="smtp-from-email-input"
                      type="email"
                      placeholder="noreply@yourcompany.com"
                      value={smtpSettings.from_email}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="from-name">From Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="from-name"
                      data-testid="smtp-from-name-input"
                      placeholder="Your Company Name"
                      value={smtpSettings.from_name}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* TLS Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="use-tls" className="text-sm font-medium">Security Mode</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {smtpSettings.smtp_port === 465 
                        ? 'Port 465 uses SSL automatically (this toggle is ignored)' 
                        : smtpSettings.use_tls 
                          ? 'STARTTLS encryption enabled (recommended for port 587)'
                          : 'No encryption — not recommended for production'}
                    </p>
                  </div>
                  <Switch
                    id="use-tls"
                    data-testid="smtp-tls-switch"
                    checked={smtpSettings.smtp_port === 465 ? true : smtpSettings.use_tls}
                    disabled={smtpSettings.smtp_port === 465}
                    onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, use_tls: checked })}
                  />
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSaveSmtp}
                  disabled={savingSmtp}
                  className="w-full sm:w-auto"
                  data-testid="smtp-save-btn"
                >
                  {savingSmtp ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save size={16} className="mr-2" /> Save Email Settings</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Test Email Card */}
            <Card className={!smtpConfigured ? 'opacity-60 pointer-events-none' : ''}>
              <CardHeader>
                <CardTitle>Send Test Email</CardTitle>
                <CardDescription>
                  {smtpConfigured
                    ? 'Verify your configuration by sending a test email'
                    : 'Save your SMTP settings first to enable testing'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="test-email-addr">Recipient Email</Label>
                  <Input
                    id="test-email-addr"
                    data-testid="smtp-test-email-input"
                    type="email"
                    placeholder="test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="mt-1"
                    disabled={!smtpConfigured}
                  />
                </div>
                <div>
                  <Label htmlFor="test-message">Custom Message (optional)</Label>
                  <Textarea
                    id="test-message"
                    data-testid="smtp-test-message-input"
                    placeholder="Add an optional message to include in the test email..."
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    className="mt-1"
                    rows={3}
                    disabled={!smtpConfigured}
                  />
                </div>
                <Button
                  onClick={handleTestEmail}
                  disabled={testingEmail || !smtpConfigured}
                  variant={smtpVerified ? 'outline' : 'default'}
                  data-testid="smtp-test-send-btn"
                >
                  {testingEmail ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Send size={16} className="mr-2" /> Send Test Email</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography">
          <Card>
            <CardHeader>
              <CardTitle>Typography</CardTitle>
              <CardDescription>Choose fonts for your application</CardDescription>
            </CardHeader>
            <CardContent>
              <div>
                <Label>Font Family</Label>
                <Select 
                  value={settings?.font_family || 'System'} 
                  onValueChange={(v) => updateSetting('font_family', v)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONTS.map(font => (
                      <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-6 p-6 border rounded-lg" style={{ fontFamily: settings?.font_family || 'System' }}>
                <h4 className="text-2xl font-bold mb-2">Preview Text</h4>
                <p className="text-gray-600">
                  The quick brown fox jumps over the lazy dog. 
                  This sample text demonstrates how your chosen font will appear throughout the application.
                </p>
                <button 
                  className="mt-4 px-4 py-2 rounded text-white"
                  style={{ backgroundColor: settings?.primary_color || '#3B82F6' }}
                >
                  Sample Button
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* UI Styles Tab */}
        <TabsContent value="ui">
          <Card>
            <CardHeader>
              <CardTitle>UI Styles</CardTitle>
              <CardDescription>Customize UI element styles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <Label>Button Style</Label>
                  <Select 
                    value={settings?.button_style || 'rounded'} 
                    onValueChange={(v) => updateSetting('button_style', v)}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BUTTON_STYLES.map(style => (
                        <SelectItem key={style} value={style}>
                          {style.charAt(0).toUpperCase() + style.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Card Style</Label>
                  <Select 
                    value={settings?.card_style || 'elevated'} 
                    onValueChange={(v) => updateSetting('card_style', v)}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CARD_STYLES.map(style => (
                        <SelectItem key={style} value={style}>
                          {style.charAt(0).toUpperCase() + style.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Icon Style</Label>
                  <Select 
                    value={settings?.icon_style || 'filled'} 
                    onValueChange={(v) => updateSetting('icon_style', v)}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_STYLES.map(style => (
                        <SelectItem key={style} value={style}>
                          {style.charAt(0).toUpperCase() + style.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Button Preview */}
              <div className="p-6 border rounded-lg">
                <h4 className="font-semibold mb-4">Button Styles Preview</h4>
                <div className="flex gap-3">
                  <button 
                    className={`px-4 py-2 text-white ${
                      settings?.button_style === 'pill' ? 'rounded-full' :
                      settings?.button_style === 'square' ? 'rounded-none' : 'rounded-lg'
                    }`}
                    style={{ backgroundColor: settings?.primary_color || '#3B82F6' }}
                  >
                    Primary
                  </button>
                  <button 
                    className={`px-4 py-2 text-white ${
                      settings?.button_style === 'pill' ? 'rounded-full' :
                      settings?.button_style === 'square' ? 'rounded-none' : 'rounded-lg'
                    }`}
                    style={{ backgroundColor: settings?.secondary_color || '#10B981' }}
                  >
                    Secondary
                  </button>
                  <button 
                    className={`px-4 py-2 border-2 ${
                      settings?.button_style === 'pill' ? 'rounded-full' :
                      settings?.button_style === 'square' ? 'rounded-none' : 'rounded-lg'
                    }`}
                    style={{ borderColor: settings?.primary_color || '#3B82F6', color: settings?.primary_color || '#3B82F6' }}
                  >
                    Outline
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Domain Tab */}
        <TabsContent value="domain">
          <Card>
            <CardHeader>
              <CardTitle>Custom Domain</CardTitle>
              <CardDescription>Use your own domain for the application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Custom Domain</Label>
                <Input
                  placeholder="app.yourcompany.com"
                  value={settings?.custom_domain || ''}
                  onChange={(e) => updateSetting('custom_domain', e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter your subdomain (e.g., app.yourcompany.com)
                </p>
              </div>

              {settings?.custom_domain && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">DNS Configuration Required</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Add these DNS records to your domain provider:
                  </p>
                  <div className="space-y-2 font-mono text-sm">
                    <div className="p-2 bg-white rounded border">
                      <span className="text-gray-500">Type:</span> CNAME<br/>
                      <span className="text-gray-500">Name:</span> {settings.custom_domain}<br/>
                      <span className="text-gray-500">Value:</span> movemate.tech
                    </div>
                  </div>
                  <Badge variant={settings?.domain_verified ? 'default' : 'secondary'} className="mt-4">
                    {settings?.domain_verified ? 'Verified' : 'Pending Verification'}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mobile App Tab */}
        <TabsContent value="app">
          <Card>
            <CardHeader>
              <CardTitle>Mobile App Branding</CardTitle>
              <CardDescription>Customize your white-label mobile app</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>App Name</Label>
                <Input
                  placeholder="Your App Name"
                  value={settings?.app_name || 'MoveMate'}
                  onChange={(e) => updateSetting('app_name', e.target.value)}
                  className="mt-2"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>App Icon URL</Label>
                  <Input
                    placeholder="https://example.com/app-icon.png"
                    value={settings?.app_icon_url || ''}
                    onChange={(e) => updateSetting('app_icon_url', e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">1024x1024px PNG</p>
                </div>
                <div>
                  <Label>Splash Screen URL</Label>
                  <Input
                    placeholder="https://example.com/splash.png"
                    value={settings?.splash_screen_url || ''}
                    onChange={(e) => updateSetting('splash_screen_url', e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">2048x2048px PNG</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom CSS Tab */}
        <TabsContent value="css">
          <Card>
            <CardHeader>
              <CardTitle>Custom CSS</CardTitle>
              <CardDescription>Add custom CSS for advanced styling (max 10,000 characters)</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder={`/* Your custom CSS */\n.my-class {\n  color: red;\n}`}
                value={settings?.custom_css || ''}
                onChange={(e) => updateSetting('custom_css', e.target.value)}
                className="font-mono text-sm min-h-[300px]"
                maxLength={10000}
              />
              <p className="text-xs text-gray-500 mt-2">
                {(settings?.custom_css || '').length}/10,000 characters
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WhiteLabelSettings;
